import React, { useState, useEffect } from "react";
import { ArrowLeft, Calendar, MapPin, Clock, Share2, Copy, Check, Facebook, Twitter, MessageCircle, QrCode, Download, Bell } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Cart } from "./Cart";
import { EventSEO } from "./SEO";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EventDetails = ({ 
  event, 
  onBack, 
  onSelectTickets,
  cartItems,
  onRemoveFromCart,
  onCheckout,
  onViewOrganizerPage
}) => {
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ name: "", email: "", phone: "" });
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);
  
  // Check if event is sold out
  const isSoldOut = event.sold_out || (event.available_tickets !== undefined && event.available_tickets <= 0);

  // Check if event is past
  const isEventPast = (() => {
    const months = {
      "janvier": 0, "février": 1, "mars": 2, "avril": 3, "mai": 4, "juin": 5,
      "juillet": 6, "août": 7, "septembre": 8, "octobre": 9, "novembre": 10, "décembre": 11
    };
    const match = (event.date || "").match(/(\d{1,2})\s+(\w+)\s+(\d{4})/i);
    if (match) {
      const month = months[match[2].toLowerCase()];
      if (month !== undefined) {
        const eventDate = new Date(parseInt(match[3]), month, parseInt(match[1]), 23, 59);
        return eventDate < new Date();
      }
    }
    return false;
  })();

  // If event is past and has an organizer, redirect to organizer page
  useEffect(() => {
    if (isEventPast && event.organizer_id && onViewOrganizerPage) {
      toast.info("Cet événement est terminé. Voici la page de l'organisateur.");
      onViewOrganizerPage(event.organizer_id);
    }
  }, [isEventPast, event.organizer_id]);

  useEffect(() => {
    if (isSoldOut && event.id) {
      axios.get(`${API}/events/${event.id}/waitlist/count`)
        .then(res => setWaitlistCount(res.data.count || 0))
        .catch(() => {});
    }
  }, [isSoldOut, event.id]);

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistForm.name || !waitlistForm.email) {
      toast.error("Nom et email requis");
      return;
    }
    setWaitlistSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${event.id}/waitlist`, waitlistForm);
      setWaitlistJoined(true);
      toast.success(`Inscrit ! Position : ${res.data.position}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // Generate the shareable URL for this event
  const eventUrl = `${window.location.origin}/event/${event.id}`;
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Impossible de copier le lien");
    }
  };

  const handleDownloadQR = async () => {
    const qrElement = document.getElementById(`event-qr-${event.id}`);
    if (!qrElement) return;
    
    try {
      const canvas = await html2canvas(qrElement, {
        backgroundColor: '#ffffff',
        scale: 3
      });
      
      const link = document.createElement('a');
      link.download = `TIKA-${event.title.replace(/\s+/g, '-')}-QRCode.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success("QR Code téléchargé !");
    } catch (err) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleShare = async () => {
    // Use native share if available (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${event.title} - ${event.artist}`,
          text: `🎵 ${event.title} avec ${event.artist}\n📅 ${event.date}\n📍 ${event.venue}\n\nRéserve tes billets sur TIKA !`,
          url: eventUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowShareMenu(true);
        }
      }
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareOnTwitter = () => {
    const text = `🎵 ${event.title} avec ${event.artist} - ${event.date} à ${event.venue}. Réserve tes billets !`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(eventUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareOnWhatsApp = () => {
    const text = `🎵 *${event.title}* avec *${event.artist}*\n📅 ${event.date}\n📍 ${event.venue}\n\nRéserve tes billets sur TIKA !\n${eventUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-background/40">
      {/* Dynamic SEO for this event */}
      <EventSEO event={event} />
      
      {/* Header */}
      <header className="border-b border-primary/10 sticky top-0 bg-background/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              data-testid="back-to-events-btn"
              variant="ghost"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Button>
            <div className="flex items-center gap-2">
              {/* QR Code Button */}
              <Button
                data-testid="qr-code-btn"
                variant="outline"
                size="sm"
                onClick={() => setShowQRCode(!showQRCode)}
                className="flex items-center gap-2"
              >
                <QrCode className="w-4 h-4" />
                <span className="hidden sm:inline">QR Code</span>
              </Button>
              
              {/* Share Button */}
              <div className="relative">
                <Button
                  data-testid="share-event-btn"
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                  className="flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Partager</span>
                </Button>
                
                {/* Share Menu Dropdown */}
                {showShareMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-background border border-border rounded-lg shadow-xl z-50 p-2">
                    <button
                      onClick={handleCopyLink}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-colors text-left"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      <span className="text-sm">{copied ? "Copié !" : "Copier le lien"}</span>
                    </button>
                    <button
                      onClick={shareOnWhatsApp}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-colors text-left"
                    >
                      <MessageCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">WhatsApp</span>
                    </button>
                    <button
                      onClick={shareOnFacebook}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-colors text-left"
                    >
                      <Facebook className="w-4 h-4 text-blue-600" />
                      <span className="text-sm">Facebook</span>
                    </button>
                    <button
                      onClick={shareOnTwitter}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-colors text-left"
                    >
                      <Twitter className="w-4 h-4 text-sky-500" />
                      <span className="text-sm">Twitter / X</span>
                    </button>
                  </div>
                )}
              </div>
              
              <Cart
                items={cartItems}
                onRemoveItem={onRemoveFromCart}
                onCheckout={onCheckout}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Click outside to close share menu */}
      {showShareMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowShareMenu(false)}
        />
      )}

      {/* QR Code Modal */}
      {showQRCode && (
        <>
          <div 
            className="fixed inset-0 bg-black/60 z-50" 
            onClick={() => setShowQRCode(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-800 mb-2">QR Code de l'événement</h3>
                <p className="text-sm text-gray-500 mb-4">Scannez pour accéder à l'événement</p>
                
                <div 
                  id={`event-qr-modal-${event.id}`}
                  className="bg-white p-6 rounded-xl border-2 border-gray-100 inline-block mb-4"
                >
                  <QRCode
                    value={eventUrl}
                    size={200}
                    level="H"
                  />
                  <div className="mt-4">
                    <p className="text-sm font-bold text-gray-800">{event.title}</p>
                    <p className="text-xs text-gray-500">{event.artist}</p>
                    <p className="text-xs text-gray-400 mt-1">{event.date} • {event.venue}</p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowQRCode(false)}
                    className="flex-1"
                  >
                    Fermer
                  </Button>
                  <Button
                    onClick={handleDownloadQR}
                    className="flex-1 bg-primary text-primary-foreground"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Event Flyer - Clean, no text overlay */}
      <div className="relative">
        <ImageWithFallback
          src={event.image}
          alt={event.title}
          className={`w-full object-contain max-h-[70vh] ${isSoldOut ? 'grayscale-[30%]' : ''}`}
        />
        
        {/* SOLD OUT Overlay */}
        {isSoldOut && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div 
              className="bg-red-600 text-white px-8 py-4 rounded-lg font-bold text-2xl sm:text-3xl uppercase tracking-widest transform -rotate-12 shadow-2xl border-4 border-red-400"
              data-testid="event-sold-out-badge"
            >
              COMPLET
            </div>
          </div>
        )}
      </div>

      {/* Event Info - Below the image */}
      <div className="px-4 sm:px-6 py-6 max-w-4xl mx-auto w-full">
        <div className="inline-block px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full mb-3">
          {event.category}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-wide mb-2">
          {event.title}
        </h1>
        <p className="text-base text-muted-foreground mb-6">Par {event.artist}</p>

        {/* Event Details */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="md:col-span-2 space-y-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-muted/20 rounded-lg">
                <Calendar className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <div className="font-medium">{event.date}</div>
                  {event.time && <div className="text-sm text-muted-foreground">{event.time}</div>}
                </div>
              </div>

              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([event.venue, event.location].filter(Boolean).join(', '))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-muted/20 rounded-lg group"
                data-testid="event-location-link"
              >
                <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <div className="font-medium group-hover:underline">{event.venue}</div>
                  {event.location && <div className="text-sm text-muted-foreground group-hover:underline">{event.location}</div>}
                </div>
              </a>
            </div>

            <div className="glass-card rounded-lg p-6">
              <h2 className="text-lg font-bold mb-3">À propos</h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {event.description || `Rejoignez-nous pour une soirée inoubliable avec ${event.artist}. Une expérience musicale unique qui célèbre la richesse de la culture ${event.category}. Ne manquez pas cet événement exceptionnel !`}
              </p>
            </div>

            {event.conditions && (
              <div className="glass-card rounded-lg p-6" data-testid="event-conditions">
                <h2 className="text-lg font-bold mb-3">Conditions</h2>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{event.conditions}</p>
              </div>
            )}

            {/* Share section for mobile */}
            <div className="glass-card rounded-lg p-6 md:hidden">
              <h3 className="text-lg font-bold mb-4">Partager cet événement</h3>
              <div className="flex gap-3">
                <Button variant="outline" size="sm" onClick={shareOnWhatsApp} className="flex-1">
                  <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
                  WhatsApp
                </Button>
                <Button variant="outline" size="sm" onClick={shareOnFacebook} className="flex-1">
                  <Facebook className="w-4 h-4 mr-2 text-blue-600" />
                  Facebook
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopyLink} className="flex-1">
                  {copied ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? "Copié" : "Lien"}
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar - Booking */}
          <div className="md:col-span-1">
            <div className="glass-card rounded-lg p-6 sticky top-24 space-y-6">
              <div>
                <div className="text-sm text-muted-foreground mb-2">{event.price > 0 ? "À partir de" : ""}</div>
                <div className={`text-4xl font-bold mb-1 ${isSoldOut ? 'text-gray-500 line-through' : 'text-primary'}`}>
                  {event.price > 0 ? `${event.price}€` : "Gratuit"}
                </div>
                {event.price > 0 && <div className="text-sm text-muted-foreground">par billet</div>}
              </div>

              {isSoldOut ? (
                <div className="space-y-4">
                  <Button
                    data-testid="sold-out-btn"
                    disabled
                    className="w-full bg-gray-600 text-gray-300 text-lg py-6 cursor-not-allowed"
                  >
                    Événement complet
                  </Button>
                  
                  {waitlistJoined ? (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                      <Bell className="w-5 h-5 mx-auto mb-2 text-green-500" />
                      <p className="text-sm text-green-400 font-medium">Vous êtes sur la liste d'attente</p>
                      <p className="text-xs text-muted-foreground mt-1">Vous serez notifié dès qu'une place se libère</p>
                    </div>
                  ) : showWaitlist ? (
                    <form onSubmit={handleJoinWaitlist} className="space-y-3" data-testid="waitlist-form">
                      <p className="text-xs text-muted-foreground">Soyez notifié si des places se libèrent :</p>
                      <Input
                        placeholder="Votre nom"
                        value={waitlistForm.name}
                        onChange={(e) => setWaitlistForm({...waitlistForm, name: e.target.value})}
                        required
                        data-testid="waitlist-name-input"
                      />
                      <Input
                        type="email"
                        placeholder="Votre email"
                        value={waitlistForm.email}
                        onChange={(e) => setWaitlistForm({...waitlistForm, email: e.target.value})}
                        required
                        data-testid="waitlist-email-input"
                      />
                      <Input
                        type="tel"
                        placeholder="Téléphone (optionnel)"
                        value={waitlistForm.phone}
                        onChange={(e) => setWaitlistForm({...waitlistForm, phone: e.target.value})}
                        data-testid="waitlist-phone-input"
                      />
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={waitlistSubmitting}
                        data-testid="waitlist-submit-btn"
                      >
                        {waitlistSubmitting ? "Inscription..." : "M'inscrire sur la liste"}
                      </Button>
                    </form>
                  ) : (
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        className="w-full border-primary/50 text-primary hover:bg-primary/10"
                        onClick={() => setShowWaitlist(true)}
                        data-testid="join-waitlist-btn"
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Rejoindre la liste d'attente
                      </Button>
                      {waitlistCount > 0 && (
                        <p className="text-center text-xs text-muted-foreground">
                          {waitlistCount} personne{waitlistCount > 1 ? 's' : ''} en attente
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  data-testid="select-tickets-btn"
                  onClick={onSelectTickets}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6"
                >
                  Sélectionner vos billets
                </Button>
              )}

              <div className="pt-4 border-t border-border space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Confirmation</span>
                  <span className="text-foreground">Instantanée</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">E-billet</span>
                  <span className="text-foreground">Mobile</span>
                </div>
              </div>

              {/* Share link display */}
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Lien de l'événement</div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={eventUrl}
                    readOnly
                    className="flex-1 text-xs bg-muted/30 border border-border rounded px-2 py-1.5 truncate"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="shrink-0"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </Button>
                </div>
              </div>

              {/* QR Code Section */}
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-3">QR Code de l'événement</div>
                <div 
                  id={`event-qr-${event.id}`}
                  className="bg-white p-4 rounded-lg flex flex-col items-center"
                >
                  <QRCode
                    value={eventUrl}
                    size={120}
                    level="H"
                    className="mb-2"
                  />
                  <p className="text-xs text-gray-600 font-medium text-center">{event.title}</p>
                  <p className="text-xs text-gray-400">{event.date}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadQR}
                  className="w-full mt-3"
                >
                  <Download className="w-3 h-3 mr-2" />
                  Télécharger QR Code
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
